import { config, hasCognito } from './config'

/**
 * OIDC authorization code + PKCE against the Cognito Hosted UI (requirement 1,
 * design SS3.1 / D8).
 *
 * The public client has no secret, so PKCE is the only thing binding the
 * redirect to this browser: the verifier never leaves `sessionStorage`, and an
 * intercepted code is useless without it.
 */

const TOKEN_KEY = 'vmoney.tokens'
const VERIFIER_KEY = 'vmoney.pkce'
const STATE_KEY = 'vmoney.state'
const RETURN_KEY = 'vmoney.return'

export interface Tokens {
  idToken: string
  accessToken: string
  refreshToken?: string
  /** Epoch ms. Derived from `expires_in` at the moment of exchange. */
  expiresAt: number
}

export interface IdClaims {
  sub: string
  email?: string
  name?: string
  exp: number
}

/**
 * Tokens live in localStorage, which is XSS-reachable -- an accepted risk (D8)
 * mitigated by a strict CSP, one-hour access tokens and refresh rotation. The
 * PKCE verifier deliberately does *not*: it belongs to a single login attempt,
 * so sessionStorage is both sufficient and safer.
 */
export function loadTokens(): Tokens | undefined {
  const raw = localStorage.getItem(TOKEN_KEY)
  if (!raw) return undefined
  try {
    const parsed = JSON.parse(raw) as Tokens
    return parsed.idToken && parsed.expiresAt ? parsed : undefined
  } catch {
    // A corrupt entry would otherwise wedge the app on every load.
    localStorage.removeItem(TOKEN_KEY)
    return undefined
  }
}

export function storeTokens(tokens: Tokens): void {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens))
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY)
}

/** Decode the id token payload. Never a substitute for the API's verification. */
export function decodeIdToken(idToken: string): IdClaims | undefined {
  const parts = idToken.split('.')
  if (parts.length !== 3 || !parts[1]) return undefined
  try {
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    // The payload is UTF-8; `atob` yields latin1, so a non-ASCII display name
    // would arrive mangled without this round-trip.
    const text = decodeURIComponent(
      Array.from(json, (ch) => `%${ch.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
    )
    return JSON.parse(text) as IdClaims
  } catch {
    return undefined
  }
}

function base64url(bytes: Uint8Array): string {
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

async function pkcePair(): Promise<{ verifier: string; challenge: string }> {
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)))
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return { verifier, challenge: base64url(new Uint8Array(digest)) }
}

/** Send the browser to the Hosted UI. `returnTo` survives the round trip. */
export async function beginLogin(returnTo = '/'): Promise<void> {
  if (!hasCognito()) {
    throw new Error('Cognito is not configured; set VITE_COGNITO_DOMAIN and VITE_COGNITO_CLIENT_ID')
  }
  const { verifier, challenge } = await pkcePair()
  const state = base64url(crypto.getRandomValues(new Uint8Array(16)))
  sessionStorage.setItem(VERIFIER_KEY, verifier)
  sessionStorage.setItem(STATE_KEY, state)
  sessionStorage.setItem(RETURN_KEY, returnTo)

  const url = new URL(`https://${config.cognitoDomain}/oauth2/authorize`)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('client_id', config.cognitoClientId)
  url.searchParams.set('redirect_uri', config.redirectUri)
  url.searchParams.set('scope', 'openid email profile')
  url.searchParams.set('state', state)
  url.searchParams.set('code_challenge', challenge)
  url.searchParams.set('code_challenge_method', 'S256')
  // Google is the only IdP (requirement 1), so skip the Hosted UI's chooser.
  url.searchParams.set('identity_provider', 'Google')
  window.location.assign(url.toString())
}

export interface CallbackResult {
  tokens: Tokens
  returnTo: string
}

/**
 * Complete the redirect. Returns `undefined` when the URL is not a callback, so
 * the login page can render normally on a direct visit.
 */
export async function completeLogin(search: string): Promise<CallbackResult | undefined> {
  const params = new URLSearchParams(search)
  const error = params.get('error')
  if (error) {
    throw new Error(params.get('error_description') ?? error)
  }
  const code = params.get('code')
  if (!code) return undefined

  const expectedState = sessionStorage.getItem(STATE_KEY)
  // A mismatched state is a CSRF attempt or a stale tab; either way the code is
  // not one this browser asked for.
  if (!expectedState || params.get('state') !== expectedState) {
    throw new Error('Login state did not match. Please try signing in again.')
  }
  const verifier = sessionStorage.getItem(VERIFIER_KEY)
  if (!verifier) {
    throw new Error('This login was started in a different tab. Please try again.')
  }

  const tokens = await exchange({
    grant_type: 'authorization_code',
    client_id: config.cognitoClientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: verifier,
  })

  const returnTo = sessionStorage.getItem(RETURN_KEY) ?? '/'
  sessionStorage.removeItem(VERIFIER_KEY)
  sessionStorage.removeItem(STATE_KEY)
  sessionStorage.removeItem(RETURN_KEY)
  storeTokens(tokens)
  return { tokens, returnTo }
}

/**
 * Exchange the refresh token for a fresh access token.
 *
 * Cognito rotates the refresh token when rotation is enabled but does not always
 * return a new one, so the old one is kept when the response omits it -- dropping
 * it would silently sign the user out an hour later.
 */
export async function refreshTokens(refreshToken: string): Promise<Tokens> {
  const tokens = await exchange({
    grant_type: 'refresh_token',
    client_id: config.cognitoClientId,
    refresh_token: refreshToken,
  })
  const merged: Tokens = { ...tokens, refreshToken: tokens.refreshToken ?? refreshToken }
  storeTokens(merged)
  return merged
}

async function exchange(body: Record<string, string>): Promise<Tokens> {
  const res = await fetch(`https://${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body).toString(),
  })
  const json = (await res.json().catch(() => ({}))) as {
    id_token?: string
    access_token?: string
    refresh_token?: string
    expires_in?: number
    error?: string
    error_description?: string
  }
  if (!res.ok || !json.id_token || !json.access_token) {
    throw new Error(json.error_description ?? json.error ?? `Token exchange failed (${res.status})`)
  }
  return {
    idToken: json.id_token,
    accessToken: json.access_token,
    ...(json.refresh_token ? { refreshToken: json.refresh_token } : {}),
    // Sixty seconds of slack, so a request started just before expiry does not
    // arrive just after it.
    expiresAt: Date.now() + ((json.expires_in ?? 3600) - 60) * 1000,
  }
}

/** Clear local state and end the Cognito session, so the next login re-consents. */
export function logout(): void {
  clearTokens()
  if (!hasCognito()) {
    window.location.assign('/login')
    return
  }
  const url = new URL(`https://${config.cognitoDomain}/logout`)
  url.searchParams.set('client_id', config.cognitoClientId)
  url.searchParams.set('logout_uri', new URL('/login', window.location.origin).toString())
  window.location.assign(url.toString())
}
